import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast, Toaster } from "sonner";
import { ArrowRight, ArrowLeft, Check, ShieldCheck, Sparkles, Clock, Car, Home, Building2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { trackEvent, getAttribution } from "@/lib/analytics";
import { useNavigate } from "react-router-dom";
import { SERVICES, getServiceConfig, buildProjectDetails, isQuestionVisible } from "@/data/mockupServices";
import AutomotiveTintForm from "@/components/AutomotiveTintForm";
import ResidentialTintForm from "@/components/ResidentialTintForm";

const SOURCE = ["Google", "Instagram / Facebook", "Referral", "Saw a wrap on the road", "Other"];
const steps = ["Service", "Contact", "Details"];

const initialContact = { full_name: "", email: "", phone: "", business_name: "" };

export default function Mockup() {
  const [step, setStep] = useState(0);
  const [contact, setContact] = useState(initialContact);
  const [serviceId, setServiceId] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [answers, setAnswers] = useState({});
  const [budget, setBudget] = useState("");
  const [timeline, setTimeline] = useState("");
  const [source, setSource] = useState("");
  const [freeText, setFreeText] = useState("");
  const [loading, setLoading] = useState(false);
  const [tintType, setTintType] = useState("");
  const navigate = useNavigate();

  const config = serviceId ? getServiceConfig(serviceId) : null;
  const isTint = serviceId === "window-tint";
  // Window Tint bypasses the generic wizard after step 1 — it drives the
  // already-built tint forms (auto form for vehicles, residential form otherwise).
  const tintHijack = isTint && step >= 1;

  useEffect(() => { trackEvent("mockup_form_step_view", { step: step + 1, step_name: steps[step] }); }, [step]);

  // Reset service-specific answers when switching services
  useEffect(() => {
    setAnswers({});
    setVehicle("");
    setBudget("");
    setTimeline("");
    setFreeText("");
    setTintType("");
  }, [serviceId]);

  const setContactField = (k) => (e) => setContact({ ...contact, [k]: e.target.value });

  const canNext =
    (step === 0 && serviceId) ||
    (step === 1 && contact.full_name && contact.email && contact.phone) ||
    (step === 2 && step3Complete());

  function step3Complete() {
    if (!config) return false;
    if (config.vehicleField && isQuestionVisible(config.vehicleField, answers) && !vehicle.trim()) return false;
    for (const q of (config.questions || []).filter((q) => isQuestionVisible(q, answers))) {
      if (!q.required) continue;
      const a = answers[q.key];
      if (a == null || a === "" || (Array.isArray(a) && a.length === 0)) return false;
    }
    return true;
  }

  const submit = async () => {
    if (!config) return;
    setLoading(true);
    try {
      const payload = {
        full_name: contact.full_name.trim(),
        email: contact.email.trim(),
        phone: contact.phone.trim(),
        business_name: contact.business_name.trim() || undefined,
        project_type: config.label,
        vehicle_type: config.vehicleField && isQuestionVisible(config.vehicleField, answers) && vehicle.trim() ? vehicle.trim() : undefined,
        project_details: buildProjectDetails(config, answers, freeText),
        budget_range: budget || undefined,
        timeline: timeline || undefined,
        referral_source: source || undefined,
        ...getAttribution(),
      };
      await api.post("/leads", payload);
      toast.success("Mockup request received!");
      const submissionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      navigate("/mockup/thank-you", {
        state: {
          fromSubmit: true,
          submissionId,
          service: config.label,
          vehicle: payload.vehicle_type || "n/a",
          has_business: Boolean(payload.business_name),
          source: source || "not_provided",
        },
        replace: true,
      });
    } catch (err) {
      trackEvent("mockup_form_error", { message: formatApiErrorDetail(err.response?.data?.detail) });
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-mockup" className="pt-32 pb-32 bg-[var(--bg-2)] min-h-screen">
      <Toaster richColors position="top-right" />
      <div className="wrap grid md:grid-cols-12 gap-12">
        {/* Left copy */}
        <aside className="md:col-span-5 md:sticky md:top-32 self-start">
          <div className="eyebrow eyebrow-red">Free · No obligation</div>
          <h1 className="h-display text-5xl md:text-6xl uppercase mt-4">
            Your <span className="italic font-normal">free</span> project quote.
          </h1>
          <p className="mt-6 text-neutral-600 leading-relaxed">
            Tell us what you&apos;re looking to do. Dave personally reviews every request and comes back with a real quote — actual project, actual numbers, no guesswork — so you know exactly where you stand before you commit.
          </p>

          <div className="mt-10 space-y-4">
            {[
              { icon: Clock, t: "Takes 90 seconds", d: "Three quick steps. Questions tailored to your project." },
              { icon: ShieldCheck, t: "Zero pressure", d: "No pushy calls. No pipeline nonsense." },
              { icon: Sparkles, t: "Actually customized", d: "Not a template. A real proposal, made for you." },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-white border border-neutral-200 grid place-items-center"><f.icon size={18} className="text-[var(--red)]"/></div>
                <div>
                  <div className="font-semibold">{f.t}</div>
                  <div className="text-sm text-neutral-500">{f.d}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-neutral-200 flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_dave-wraps-studio/artifacts/dmpaw94x_Webpage_MainImage.webp"
              alt="Dave Mitchell, owner of 928 Wraps"
              className="w-14 h-14 rounded-full object-cover"
              style={{ objectPosition: "50% 60%" }}
            />
            <div className="text-sm">
              <div className="font-semibold text-neutral-900">Dave reviews every request personally.</div>
              <div className="text-neutral-500 mt-0.5">Not a chatbot. Not an assistant. Him.</div>
            </div>
          </div>
        </aside>

        {/* Right form */}
        <div className="md:col-span-7">
          <div className="bg-white rounded-3xl border border-neutral-200 p-8 md:p-10 relative overflow-hidden">
            {/* Progress — hidden while the tint branch drives its own forms */}
            {!tintHijack && (
            <div className="flex items-center gap-3">
              {steps.map((s, i) => (
                <div key={s} className="flex items-center gap-3 flex-1">
                  <div className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold border ${i <= step ? "bg-[var(--red)] text-white border-[var(--red)]" : "bg-white text-neutral-500 border-neutral-200"}`}>{i < step ? <Check size={14}/> : i+1}</div>
                  <div className={`eyebrow ${i === step ? "text-[var(--ink)]" : ""}`}>{s}</div>
                  {i < steps.length - 1 && <div className="flex-1 h-[2px] bg-neutral-200"><div className={`h-full ${i < step ? "bg-[var(--red)]" : ""}`} style={{width: i < step ? "100%" : "0"}}/></div>}
                </div>
              ))}
            </div>
            )}

            <div className="mt-10">
              {tintHijack ? (
                <motion.div key="tint-branch" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
                  {!tintType ? (
                    <>
                      <h2 className="h-display text-3xl md:text-4xl uppercase">What are we tinting?</h2>
                      <p className="mt-2 text-sm text-neutral-500">Three quick steps and you&apos;re done — no typing until the end.</p>
                      <div className="mt-6 grid grid-cols-1 gap-3">
                        {[
                          { id: "vehicle", icon: Car, t: "My vehicle", d: "Car, truck, or SUV — ceramic tint, legal Arizona shades." },
                          { id: "home", icon: Home, t: "My home", d: "Lower cooling bills, less fade, more privacy." },
                          { id: "business", icon: Building2, t: "My business", d: "Office, storefront, or commercial glass." },
                        ].map((o) => (
                          <button
                            key={o.id}
                            type="button"
                            data-testid={`mk-tint-type-${o.id}`}
                            onClick={() => setTintType(o.id)}
                            className="text-left p-5 rounded-2xl border border-neutral-200 hover:border-neutral-400 transition-all flex items-center gap-4"
                          >
                            <span className="w-11 h-11 rounded-full bg-red-50 grid place-items-center flex-shrink-0"><o.icon size={18} className="text-[var(--red)]" /></span>
                            <span>
                              <span className="block font-semibold text-neutral-900">{o.t}</span>
                              <span className="block text-sm text-neutral-500 mt-0.5">{o.d}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                      <div className="mt-8">
                        <button type="button" data-testid="mk-tint-back" onClick={() => setStep(0)} className="btn-ghost"><ArrowLeft size={16}/> Back</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <button type="button" data-testid="mk-tint-change" onClick={() => setTintType("")} className="text-sm font-semibold text-neutral-500 hover:text-black inline-flex items-center gap-1.5 mb-6">
                        <ArrowLeft size={14}/> Back
                      </button>
                      {tintType === "vehicle" ? (
                        <AutomotiveTintForm bare source="Mockup Wizard — Window Tint (Vehicle)" />
                      ) : (
                        <ResidentialTintForm
                          bare
                          source={`Mockup Wizard — Window Tint (${tintType === "business" ? "Business" : "Home"})`}
                          projectType={tintType === "business" ? "Commercial Window Tint" : "Residential Window Tint"}
                          lpKey="mockup-window-tint"
                          testidPrefix="mk-res"
                        />
                      )}
                    </>
                  )}
                </motion.div>
              ) : (
                <>
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                    <h2 className="h-display text-3xl md:text-4xl uppercase">Now, the basics.</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                      <input data-testid="mk-name" required placeholder="Full name *" className="input-928" value={contact.full_name} onChange={setContactField("full_name")} />
                      <input data-testid="mk-business" placeholder={serviceId === "window-tint" ? "Business name (if applicable)" : "Business name"} className="input-928" value={contact.business_name} onChange={setContactField("business_name")} />
                      <input data-testid="mk-email" required type="email" placeholder="Email *" className="input-928" value={contact.email} onChange={setContactField("email")} />
                      <input data-testid="mk-phone" required placeholder="Phone *" className="input-928" value={contact.phone} onChange={setContactField("phone")} />
                    </div>
                  </motion.div>
                )}

                {step === 0 && (
                  <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                    <h2 className="h-display text-3xl md:text-4xl uppercase">What can we build for you?</h2>
                    <p className="mt-2 text-sm text-neutral-500">Pick the closest match — we&apos;ll ask a few tailored questions next.</p>
                    <div className="mt-6 grid grid-cols-1 gap-3">
                      {SERVICES.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`mk-service-${s.id}`}
                          onClick={() => setServiceId(s.id)}
                          className={`text-left p-5 rounded-2xl border transition-all ${serviceId === s.id ? "border-[var(--red)] bg-red-50/40 ring-2 ring-[var(--red)]/20" : "border-neutral-200 hover:border-neutral-400"}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="font-semibold text-neutral-900">{s.label}</div>
                              <div className="text-sm text-neutral-500 mt-1">{s.blurb}</div>
                            </div>
                            <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-1 grid place-items-center ${serviceId === s.id ? "border-[var(--red)] bg-[var(--red)]" : "border-neutral-300"}`}>
                              {serviceId === s.id && <Check size={12} className="text-white" />}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {step === 2 && config && (
                  <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                    <h2 className="h-display text-3xl md:text-4xl uppercase">A few quick details.</h2>
                    <p className="mt-2 text-sm text-neutral-500">About your <span className="font-semibold text-neutral-800">{config.label}</span> project.</p>

                    <div className="mt-6 space-y-5">
                      {config.vehicleField && isQuestionVisible(config.vehicleField, answers) && (
                        <div>
                          <label className="eyebrow">{config.vehicleField.label} *</label>
                          <input
                            data-testid="mk-vehicle"
                            required
                            placeholder={config.vehicleField.placeholder}
                            className="input-928 mt-2"
                            value={vehicle}
                            onChange={(e) => setVehicle(e.target.value)}
                          />
                        </div>
                      )}

                      {(config.questions || []).filter((q) => isQuestionVisible(q, answers)).map((q) => (
                        <div key={q.key}>
                          <label className="eyebrow">{q.label}{q.required && " *"}</label>
                          {q.type === "select" && (
                            <select
                              data-testid={`mk-q-${q.key}`}
                              required={q.required}
                              className="input-928 mt-2"
                              value={answers[q.key] || ""}
                              onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                            >
                              <option value="">Select…</option>
                              {q.options.map((o) => <option key={o}>{o}</option>)}
                            </select>
                          )}
                          {q.type === "textarea" && (
                            <textarea
                              data-testid={`mk-q-${q.key}`}
                              required={q.required}
                              placeholder={q.placeholder}
                              className="input-928 mt-2"
                              value={answers[q.key] || ""}
                              onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                            />
                          )}
                          {q.type === "text" && (
                            <input
                              data-testid={`mk-q-${q.key}`}
                              required={q.required}
                              placeholder={q.placeholder}
                              className="input-928 mt-2"
                              value={answers[q.key] || ""}
                              onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                            />
                          )}
                          {q.type === "multi" && (
                            <div className="mt-2 flex flex-wrap gap-2" data-testid={`mk-q-${q.key}`}>
                              {q.options.map((o) => {
                                const arr = Array.isArray(answers[q.key]) ? answers[q.key] : [];
                                const active = arr.includes(o);
                                return (
                                  <button
                                    key={o}
                                    type="button"
                                    onClick={() => {
                                      const next = active ? arr.filter((x) => x !== o) : [...arr, o];
                                      setAnswers({ ...answers, [q.key]: next });
                                    }}
                                    className={`px-4 py-2 rounded-full border text-sm transition ${active ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"}`}
                                  >
                                    {o}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}

                      {config.askBudget && (
                        <div>
                          <label className="eyebrow">Budget range</label>
                          <select data-testid="mk-budget" className="input-928 mt-2" value={budget} onChange={(e) => setBudget(e.target.value)}>
                            <option value="">Select…</option>
                            {(config.budgetOptions || []).map((t) => <option key={t}>{t}</option>)}
                          </select>
                        </div>
                      )}

                      {config.askTimeline && (
                        <div>
                          <label className="eyebrow">Timeline</label>
                          <select data-testid="mk-timeline" className="input-928 mt-2" value={timeline} onChange={(e) => setTimeline(e.target.value)}>
                            <option value="">Select…</option>
                            {["ASAP", "Within 2 weeks", "1–2 months", "Just exploring"].map((t) => <option key={t}>{t}</option>)}
                          </select>
                        </div>
                      )}

                      <div>
                        <label className="eyebrow">Anything else Dave should know?</label>
                        <textarea
                          data-testid="mk-freetext"
                          placeholder="Colors, deadlines, competitors you like, existing brand assets, or whatever else is on your mind."
                          className="input-928 mt-2"
                          value={freeText}
                          onChange={(e) => setFreeText(e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="eyebrow">How&apos;d you find us?</label>
                        <select data-testid="mk-source" className="input-928 mt-2" value={source} onChange={(e) => setSource(e.target.value)}>
                          <option value="">Select…</option>
                          {SOURCE.map((t) => <option key={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mt-10 flex items-center justify-between">
                {step > 0 ? (
                  <button data-testid="mk-back" onClick={() => setStep(step - 1)} className="btn-ghost"><ArrowLeft size={16}/> Back</button>
                ) : <span/>}

                {step < 2 ? (
                  <button data-testid="mk-next" disabled={!canNext} onClick={() => setStep(step + 1)} className={`btn-red ${!canNext ? "opacity-50 pointer-events-none" : ""}`}>Continue <ArrowRight size={16}/></button>
                ) : (
                  <button data-testid="mk-submit" disabled={loading || !canNext} onClick={submit} className={`btn-red ${loading || !canNext ? "opacity-70 pointer-events-none" : ""}`}>{loading ? "Sending…" : "Send my mockup request"} <ArrowRight size={16}/></button>
                )}
              </div>
              <p className="mt-6 text-xs text-neutral-500 text-center">We never share your info. Ever. · Dave replies within 1 business day.</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
